/**
 * Libro mayor de una cuenta (DISENO_CONTABILIDAD §7.3).
 *
 * El saldo se muestra según la naturaleza de la cuenta: en una deudora
 * (activo, gasto) positivo = saldo deudor; en una acreedora (pasivo,
 * patrimonio, ingreso) positivo = saldo acreedor. Un saldo contra su
 * naturaleza sale en rojo.
 */
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  Cargando,
  ErrorCarga,
  PageHeader,
  Select,
  TableWrap,
} from "../../components/ui";
import { hasPermission } from "../../lib/auth";
import { claseMonto, fechaLarga, pesos } from "../../lib/format";
import { useAuth } from "../auth/useAuth";
import { fetchCuentas, fetchMayor } from "./api";
import {
  AvisoProvisorio,
  BarraPeriodo,
  BotonesExportar,
  usePeriodoUrl,
} from "./componentes";

export function MayorPage(): JSX.Element {
  const { state } = useAuth();
  const { cuentaId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const url = usePeriodoUrl();
  const id = cuentaId ? Number(cuentaId) : undefined;

  const cuentas = useQuery({ queryKey: ["contabilidad", "cuentas"], queryFn: fetchCuentas });
  const q = useQuery({
    queryKey: ["contabilidad", "mayor", id, url.filtro],
    queryFn: () => fetchMayor(id as number, url.filtro),
    // Al cambiar el período se mantiene lo anterior en pantalla mientras carga.
    placeholderData: keepPreviousData,
    enabled: id !== undefined,
  });
  const m = q.data;
  const signo = m?.deudora === false ? -1 : 1;

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="space-y-3">
        <PageHeader
          title="Libro mayor"
          actions={
            <BotonesExportar
              recurso="mayor"
              params={{ ...url.filtro, ...(id !== undefined ? { cuenta_id: id } : {}) }}
              soloExcel={id === undefined}
            />
          }
        />
        <div className="max-w-xl">
          <Select
            label="Cuenta"
            value={id !== undefined ? String(id) : ""}
            onChange={(e) => navigate(`/contabilidad/mayor/${e.target.value}${search}`)}
            hint={id === undefined ? "Sin cuenta elegida, Excel descarga el mayor de todas las cuentas." : undefined}
          >
            <option value="" disabled>
              Elige una cuenta…
            </option>
            {(cuentas.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.codigo} · {c.nombre}
              </option>
            ))}
          </Select>
        </div>
        <BarraPeriodo periodo={m?.periodo} url={url} />
        <AvisoProvisorio periodo={m?.periodo} />
      </div>

      {q.isLoading && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {cuentas.data && cuentas.data.length === 0 && (
        <p className="text-sm text-neutral-600">
          {hasPermission(state, "contabilidad.cargar")
            ? "Todavía no hay cuentas: sube el libro diario en Contabilidad · Cargas."
            : "La oficina contable todavía no publica información."}
        </p>
      )}

      {m && (
        <TableWrap label="Libro mayor">
          <table className="w-full min-w-[900px] text-sm">
            <caption className="sr-only">
              Libro mayor de {m.cuenta.codigo} {m.cuenta.nombre}
            </caption>
            <thead className="bg-neutral-100 text-xs uppercase tracking-wide text-neutral-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Fecha</th>
                <th className="px-3 py-2 text-left font-medium">Comprobante</th>
                <th className="px-3 py-2 text-left font-medium">Glosa</th>
                <th className="px-3 py-2 text-left font-medium">Documento</th>
                <th className="px-3 py-2 text-right font-medium">Debe</th>
                <th className="px-3 py-2 text-right font-medium">Haber</th>
                <th className="px-3 py-2 text-right font-medium">
                  Saldo {m.deudora ? "deudor" : "acreedor"}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              <tr className="bg-neutral-50">
                <td colSpan={6} className="px-3 py-1.5 text-neutral-700">
                  Saldo inicial al {fechaLarga(m.periodo.desde)}
                </td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${claseMonto(signo * m.saldo_inicial)}`}>
                  {pesos(signo * m.saldo_inicial)}
                </td>
              </tr>
              {m.lineas.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-neutral-600">
                    Sin movimientos en el período.
                  </td>
                </tr>
              )}
              {m.lineas.map((ln, i) => (
                <tr key={`${ln.comprobante_id}-${i}`} className="hover:bg-neutral-50">
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">{fechaLarga(ln.fecha)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <Link
                      to={`/contabilidad/diario?comprobante=${ln.comprobante_id}`}
                      className="text-brand-700 hover:text-brand-800 hover:underline"
                    >
                      {ln.comprobante}
                    </Link>
                  </td>
                  <td className="px-3 py-1.5">{ln.glosa}</td>
                  <td className="px-3 py-1.5 text-neutral-600">{ln.documento ?? "—"}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                    {ln.debe ? pesos(ln.debe) : "—"}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                    {ln.haber ? pesos(ln.haber) : "—"}
                  </td>
                  <td
                    className={`whitespace-nowrap px-3 py-1.5 text-right tabular-nums ${claseMonto(signo * ln.saldo)}`}
                  >
                    {pesos(signo * ln.saldo)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-neutral-300 bg-neutral-100 font-medium">
              <tr>
                <td colSpan={4} className="px-3 py-1.5">
                  Totales y saldo final
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{pesos(m.total_debe)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{pesos(m.total_haber)}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${claseMonto(signo * m.saldo_final)}`}>
                  {pesos(signo * m.saldo_final)}
                </td>
              </tr>
            </tfoot>
          </table>
        </TableWrap>
      )}
    </div>
  );
}
